import MailOutlineIcon from '@offline-mui/icons-material/MailOutlined'
import { Alert, Autocomplete, Box, Button, Chip, Popover, Stack, TextField, Typography } from '@offline-mui/material'
import { useState } from 'react'
import { accountingRequest, jsonInit } from '../api'

// Send invoice, beside Download PDF: a dropdown to enter several email addresses. Leo builds the same PDF as the download
// and hands it to tbi-gg-mail. The agency's email is offered; any other address can be typed (Enter, comma or space adds it).

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/
const MAX_RECIPIENTS = 20

function splitAddresses(value: string) {
  return value.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean)
}

function uniqueAddresses(values: string[]) {
  const seen = new Set<string>()
  return values.filter((value) => {
    const key = value.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export default function SendInvoiceMenu({ invoice, onSent }: { invoice: any; onSent: (notice: string) => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [emails, setEmails] = useState<string[]>([])
  const [typed, setTyped] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const agencyEmails = splitAddresses(invoice?.agency?.email || '').filter((value) => EMAIL.test(value))

  function open(event: any) {
    setAnchor(event.currentTarget)
    setEmails([])
    setTyped('')
    setError('')
  }

  function changeTyped(value: string) {
    // A comma, semicolon or space ends an address, so a pasted list becomes one chip per address.
    if (/[\s,;]/.test(value)) {
      setEmails((current) => uniqueAddresses([...current, ...splitAddresses(value)]))
      setTyped('')
      return
    }
    setTyped(value)
  }

  async function send(event: any) {
    event.preventDefault()
    const to = uniqueAddresses([...emails, ...splitAddresses(typed)])
    setEmails(to)
    setTyped('')
    if (!to.length) return setError('Enter at least one email address.')
    const wrong = to.filter((value) => !EMAIL.test(value))
    if (wrong.length) return setError(`Not a valid email address: ${wrong.join(', ')}`)
    if (to.length > MAX_RECIPIENTS) return setError(`Send to up to ${MAX_RECIPIENTS} addresses at a time.`)
    setSending(true)
    setError('')
    try {
      const result = await accountingRequest(
        `/invoices/${encodeURIComponent(invoice.recordLocator)}/email`,
        jsonInit('POST', { to }),
      )
      setAnchor(null)
      const list = (result.to || to).join(', ')
      // The mail service in log mode only prints the email, so do not say it was sent.
      onSent(result.delivered === false ? `Test mode: nothing was delivered. Invoice ${invoice.invoiceNumber} would go to ${list}.` : `Invoice ${invoice.invoiceNumber} sent to ${list}.`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Button variant="contained" color="warning" size="small" startIcon={<MailOutlineIcon />} onClick={open}>
        Send invoice
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={sending ? undefined : () => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box
          component="form"
          onSubmit={send}
          // Enter in the address box only adds an address; sending takes the Send button.
          onKeyDown={(event: any) => {
            if (event.key === 'Enter' && event.target.tagName === 'INPUT') event.preventDefault()
          }}
          sx={{ p: 2, width: 420, maxWidth: '100%' }}
        >
          {/* The buttons sit above the field: the list of suggested addresses opens below it and would cover them. */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Send invoice {invoice?.invoiceNumber}
            </Typography>
            <Button size="small" disabled={sending} onClick={() => setAnchor(null)}>Cancel</Button>
            <Button type="submit" size="small" variant="contained" color="secondary" disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </Stack>
          {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={agencyEmails}
            value={emails}
            inputValue={typed}
            disabled={sending}
            onChange={(_event, value) => {
              setEmails(uniqueAddresses(value.flatMap((item) => splitAddresses(String(item)))))
              setTyped('')
            }}
            onInputChange={(_event, value, reason) => {
              if (reason === 'input') changeTyped(value)
            }}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index })
                return (
                  <Chip
                    key={key}
                    size="small"
                    label={option}
                    color={EMAIL.test(String(option)) ? 'default' : 'error'}
                    {...itemProps}
                  />
                )
              })
            }
            renderInput={(params) => (
              <TextField {...params} autoFocus label="Email addresses" placeholder={emails.length ? '' : 'name@agency.com'} />
            )}
          />
        </Box>
      </Popover>
    </>
  )
}
